import React, { useEffect, useState } from 'react';
import { recallUpsellToken } from '../../../../utils/upsellToken';
import { Typography, Button, Box, CircularProgress } from '@mui/material';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';

interface ExclusiveOfferProps {
  paymentIntentId: any;
  packageWithUpsell: {
    id: string;
    cards: number;
    entries: number;
    Upsell: {
      price: string;
      extra_cards: number;
      extra_entries: number;
    };
  };
  handleClose: () => void;
}

const ExclusiveOffer: React.FC<ExclusiveOfferProps> = ({
  paymentIntentId,
  packageWithUpsell,
  handleClose
}) => {
  const stripe = useStripe();
  const { showSnackbar } = useShowSnackbar();
  const elements = useElements();
  const { post } = useNetwork();
  const [loading, setLoading] = useState(false);

  const handleYes = async () => {
    if (!stripe || !elements) {
      showSnackbar('Stripe has failed to load.', 'error');
      return;
    }

    setLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showSnackbar('Failed to submit payment details.', 'error');
        return;
      }

      const result = await post(
        `/checkout/upsell-payment-intent`,
        // the charge is authorised by the token, not by knowing the intent id
        { upsellToken: recallUpsellToken(paymentIntentId) },
        true
      );

      const { error } = await stripe.confirmPayment({
        clientSecret: result.clientSecret,
        confirmParams: {
          return_url: `${window.location}`
        },
        redirect: 'if_required'
      });

      if (error) {
        showSnackbar('An unexpected error occurred.', 'error');
      } else {
        showSnackbar('Payment successful!', 'success');
        handleClose(); // Close the modal on success
      }
    } catch (error) {
      showSnackbar('An unexpected error occurred.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      textAlign="center"
      // bgcolor="background.paper"
      p={4}
      borderRadius={2}
      // boxShadow={3}
    >
      <Typography
        variant="h3"
        color="text.secondary"
        fontWeight="bold"
        sx={{ marginTop: '-20%', marginBottom: '10%' }}
        textAlign="center"
      >
        Thanks for your purchase!
      </Typography>
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        sx={{ pb: 2 }}
      >
        Don't miss this one-time exclusive offer!
      </Typography>
      {packageWithUpsell && (
        <>
          <Typography
            variant="body1"
            fontWeight="bold"
            color="text.secondary"
            sx={{ mt: 2 }}
          >
            Elevate your chances with an additional{' '}
            {packageWithUpsell.Upsell.extra_entries} entries and{' '}
            {packageWithUpsell.Upsell.extra_cards} Digital Collectibles.
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color="text.secondary"
            sx={{ mt: 2 }}
          >
            For just ${packageWithUpsell.Upsell.price}
          </Typography>
        </>
      )}
      <Box mt={4}>
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={handleYes}
          sx={{ marginRight: '10px' }}
        >
          {loading ? <CircularProgress size={24} /> : 'Yes'}
        </Button>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          No Thanks
        </Button>
      </Box>
    </Box>
  );
};

const ExclusiveOfferInjected: React.FC<{
  paymentIntentId: string;
  handleCloseModal: () => void;
}> = ({ paymentIntentId, handleCloseModal }) => {
  const [upsellPackage, setUpsellPackage] = useState(null);
  const { get } = useNetwork();

  useEffect(() => {
    /* If there is no offer to show, CLOSE — do not render nothing.
     *
     * This component is mounted in place of the purchase form once a payment
     * succeeds, inside a Modal with no onClose, no backdrop dismiss and no Escape.
     * So "no upsell" used to mean an empty, un-closable box sitting over a purchase
     * that had already gone through, with a page reload the only way out.
     *
     * It is not a rare path: /checkout/upsell-details 404s whenever the package
     * simply has no Upsell configured, and now also once the offer window has
     * closed — a slow 3DS challenge is enough. */
    const getUpsellDetails = async () => {
      try {
        const result = await get(
          `/checkout/upsell-details/${paymentIntentId}`,
          true
        );
        if (result?.upsellPackage) {
          setUpsellPackage(result.upsellPackage);
          return;
        }
        handleCloseModal();
      } catch (error) {
        console.error('Error fetching upsell details:', error);
        handleCloseModal();
      }
    };

    if (paymentIntentId) {
      getUpsellDetails();
    } else {
      handleCloseModal();
    }
    // handleCloseModal is recreated each render by the parent; re-running this
    // effect on it would refetch the offer on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentId]);

  return (
    <>
      {upsellPackage && (
        <Box sx={{ padding: '2rem', maxWidth: 600, margin: 'auto', mt: 10 }}>
          <ExclusiveOffer
            paymentIntentId={paymentIntentId}
            packageWithUpsell={upsellPackage}
            handleClose={handleCloseModal}
          />
        </Box>
      )}
    </>
  );
};

export default ExclusiveOfferInjected;
